/**
 * Note d'une imitation — PURE. Score 0..100 : proximité à la cible du smiley, mesurée par
 * rapport au visage AU REPOS du joueur (lu pendant le décompte), moins une pénalité pour
 * les mouvements d'une autre grimace. Le meilleur score retenu est un score TENU ~0,4 s.
 */
import type { Blendshapes } from '../detection/types';
import { clamp01, median } from '../lib/math';
import { SMILEYS, type SmileyId } from './smileys';

/** Écart au repos toléré sur un mouvement « à éviter » avant pénalité, et pénalité pleine. */
const AVOID_FREE = 0.15;
const AVOID_FULL = 0.45;
/** Part du score perdue quand tous les mouvements à éviter sont francs. */
const AVOID_WEIGHT = 0.6;
/**
 * Part minimale de la cible toujours exigée, même si le repos ne laisse presque aucune marge
 * (lunettes : yeux/sourcils déjà « ouverts » pour le modèle). Évite une cible gratuite.
 */
const MIN_TARGET_SHARE = 0.4;
/** Durée pendant laquelle une grimace doit tenir pour compter comme meilleur score. */
export const SUSTAIN_MS = 400;

const value = (bs: Blendshapes, k: string): number => bs[k] ?? 0;

/**
 * Écart exigé sur un coefficient : la cible, plafonnée à la marge restante avant 1,0 depuis le
 * repos du joueur. Sinon un repos haut (lunettes) rendrait la cible inatteignable.
 */
const effectiveTarget = (target: number, rest: number): number =>
  Math.min(target, Math.max(1 - rest, target * MIN_TARGET_SHARE));

export function imitationScore(bs: Blendshapes | null, rest: Blendshapes, id: SmileyId): number {
  if (!bs) return 0;
  const { target, avoid } = SMILEYS[id];
  const goals = Object.entries(target).filter((e): e is [string, number] => e[1] !== undefined);
  const reached =
    goals.reduce(
      (sum, [k, t]) =>
        sum + clamp01((value(bs, k) - value(rest, k)) / effectiveTarget(t, value(rest, k))),
      0,
    ) / goals.length;
  const excess =
    avoid.length === 0
      ? 0
      : avoid.reduce(
          (sum, k) => sum + clamp01((value(bs, k) - value(rest, k) - AVOID_FREE) / AVOID_FULL),
          0,
        ) / avoid.length;
  return Math.round(100 * reached * (1 - AVOID_WEIGHT * excess));
}

/** Visage au repos : médiane de chaque coefficient (images sans visage ignorées). */
export function baselineOf(frames: readonly (Blendshapes | null)[]): Record<string, number> {
  const values = new Map<string, number[]>();
  for (const bs of frames) {
    if (!bs) continue;
    for (const [k, v] of Object.entries(bs)) {
      if (v === undefined) continue;
      const list = values.get(k) ?? [];
      list.push(v);
      values.set(k, list);
    }
  }
  return Object.fromEntries([...values].map(([k, list]) => [k, median(list)]));
}

export interface ImitationSample {
  /** Score de cette image. */
  readonly live: number;
  /** Le meilleur score tenu vient de progresser (moment de prendre la photo). */
  readonly improved: boolean;
}

export interface ImitationTracker {
  push(t: number, bs: Blendshapes | null): ImitationSample;
  /** Meilleur score tenu au moins SUSTAIN_MS depuis le début. */
  best(): number;
}

export function createImitationTracker(id: SmileyId, rest: Blendshapes): ImitationTracker {
  let window: { t: number; live: number }[] = [];
  let best = 0;
  return {
    push(t, bs) {
      const live = imitationScore(bs, rest, id);
      if (!bs) {
        window = [];
        return { live, improved: false };
      }
      window.push({ t, live });
      // On garde la dernière image d'avant la fenêtre : elle prouve que la fenêtre est couverte.
      while (window.length > 1 && (window[1] as { t: number }).t <= t - SUSTAIN_MS) window.shift();
      const first = window[0] as { t: number };
      if (first.t > t - SUSTAIN_MS) return { live, improved: false };
      const held = Math.min(...window.map((s) => s.live));
      if (held <= best) return { live, improved: false };
      best = held;
      return { live, improved: true };
    },
    best: () => best,
  };
}
